import { React, useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import LoadingSpinner from "../components/LoadingSpinner";
import LabelModal from "../components/LabelModal";
import { AuthContext, AuthConsumer } from "../helpers/AuthContext";
import Sidebar from "../components/Sidebar";
import {
  BrainIcon,
  MuscleIcon,
  EyeIcon,
  HeartIcon,
  LineNoiseIcon,
  ChanNoiseIcon,
  OtherIcon,
  UnsureIcon,
} from "../components/ClassifierIcons";
import { ImageInstance } from "../utils/ImageInstance";
import "../styles/responsive.css";

// Pixel tolerance for "the horizontal scroll reached the far-right edge".
// Browsers report fractional scrollLeft/scrollWidth, so an exact-equality
// check would never be satisfied; 2px absorbs sub-pixel rounding.
const SCROLL_END_EPSILON = 2;

// Shown when the labeler tries to submit before scrolling the time course
// to its end. Kept as a constant so the "reached end" handler can clear
// exactly this message without stomping other validation messages.
const SCROLL_END_MESSAGE =
  'Please scroll through the entire time course before submitting — or click "Scroll to End" if it is clearly a brain component.';

// LabelPage holds at most three ImageInstances and uses object identity
// to keep them straight:
//   - currentInstance:    rendered (or about to be rendered) IC
//   - nextInstance:       prefetched IC, pre-warmed for instant advance
//   - submittingInstance: snapshot frozen at submit click — POST body
//                         and the success modal both read this, so a
//                         prefetch that lands during the request can't
//                         shift the displayed name out from under them.
//
// `paintedInstanceRef` is updated in the <img>'s onLoad handler, with
// the SPECIFIC instance captured by the closure. Since each instance
// also keys the <img>, React mounts a fresh DOM node per instance —
// stale onLoad events from old instances fire on unmounted nodes and
// React drops them. The submit guard checks identity:
//
//     paintedInstanceRef.current === currentInstance
//
// Without that === relation, we refuse to submit. Combined with the
// backend filename + bytes-hash cross-checks, this makes it
// impossible for a label to be saved against an IC the user didn't see.

const LabelPage = () => {
  const context = useContext(AuthContext);
  const [skipCooldown, setSkipCooldown] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The three image slots. All other "what file is showing" data
  // (filename, blob URL, hash) is contained inside the instance —
  // no parallel state to keep in sync.
  const [currentInstance, setCurrentInstance] = useState(null);
  const [nextInstance, setNextInstance] = useState(null);
  const [submittingInstance, setSubmittingInstance] = useState(null);

  const [scrollImage, setScrollImage] = useState(null);

  // Scroll-to-end gate: the labeler must reach the far-right edge of the
  // time-course trace at least ONCE before they may submit. "At least once"
  // (not "currently at the end") so they can scroll back to inspect after
  // reaching the end. Auto-satisfied when there is no trace or the trace
  // isn't wide enough to scroll. `reachedScrollEndRef` is the synchronous
  // source of truth read by the submit gate; the state mirror drives the
  // "Scroll to End" button's visibility.
  const [reachedScrollEnd, setReachedScrollEnd] = useState(false);

  // Collapsible left sidebar on the labeling screen — gives labelers more room.
  // Starts COLLAPSED every time the labeling page loads; the « / » toggle lets
  // the labeler open it during the session. Driven via a class on <body> so the
  // fixed sidebar AND the page-content margin both respond (see sidebar.css),
  // regardless of which render branch drew the sidebar.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  useEffect(() => {
    const cls = "label-sidebar-collapsed";
    if (sidebarCollapsed) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    // Leaving the labeling page must not hide the sidebar elsewhere.
    return () => document.body.classList.remove(cls);
  }, [sidebarCollapsed]);

  const [brain, setBrain] = useState(false);
  const [muscle, setMuscle] = useState(false);
  const [eye, setEye] = useState(false);
  const [heart, setHeart] = useState(false);
  const [skin, setSkin] = useState(false);
  const [linenoise, setLinenoise] = useState(false);
  const [channoise, setChannoise] = useState(false);
  const [other, setOther] = useState(false);
  const [unsure, setUnsure] = useState(false);

  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [validationError, setValidationError] = useState("");
  const [showDetailedHelp, setShowDetailedHelp] = useState(false);

  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const imageRef = useRef(null);
  const currentScrollBlobUrl = useRef(null);
  const reachedScrollEndRef = useRef(false);
  const scrollViewportRef = useRef(null);
  const scrollTrackRef = useRef(null);
  const scrollThumbRef = useRef(null);

  // Synchronous guard against parallel prefetches. The React-state
  // version (a useState boolean) lags one render behind, so two
  // prefetches kicked off in the same event tick can both pass the
  // guard and run in parallel — a sync ref closes that window.
  const prefetchingRef = useRef(false);

  // The single source of truth for "what bytes did the browser actually
  // paint?" Updated in the <img>'s onLoad with the specific instance the
  // closure captured. Stays at the previous instance until paint
  // completes; the submit guard refuses to fire on a mismatch.
  const paintedInstanceRef = useRef(null);

  // Clear all selections
  const clearAllSelections = () => {
    setBrain(false);
    setMuscle(false);
    setEye(false);
    setHeart(false);
    setSkin(false);
    setLinenoise(false);
    setChannoise(false);
    setOther(false);
    setUnsure(false);
    setValidationError("");
  };

  // Handle skip with cooldown
  const handleSkip = () => {
    setSkipCooldown(3);
    getNext();

    const countdown = setInterval(() => {
      setSkipCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(countdown);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Submit results
  // Latch "reached the end". Called from the scroll listener and the
  // Scroll-to-End button. Idempotent. Clears ONLY the scroll-gate message
  // so a pending "select a label" message is preserved.
  const markScrollEndReached = () => {
    reachedScrollEndRef.current = true;
    setReachedScrollEnd(true);
    setValidationError((prev) => (prev === SCROLL_END_MESSAGE ? "" : prev));
  };

  // Second option for obvious cases (e.g. a clear brain IC): jump the trace
  // straight to its end. The meeting agreed labelers may slide immediately
  // to the end for obvious ICs, so this satisfies the gate. We both move the
  // viewport AND latch, so it works even if the smooth-scroll is interrupted
  // or the browser throttles the scroll event.
  const handleScrollToEnd = () => {
    const vp = scrollViewportRef.current;
    if (vp) {
      const target = vp.scrollWidth - vp.clientWidth;
      if (target > 0) {
        try {
          vp.scrollTo({ left: target, behavior: "smooth" });
        } catch (_) {
          vp.scrollLeft = target;
        }
      }
    }
    markScrollEndReached();
  };

  const submitResults = async () => {
    if (!validateSelection()) {
      return;
    }

    if (isSubmitting) {
      return;
    }

    // Refuse to submit if there's no current IC (e.g., during a load
    // window between ICs). Without this guard, a fast keyboard user
    // could press a label key + Enter and the submit would race the
    // load — the original drift bug.
    if (!currentInstance) {
      setValidationError("Please wait for the next image to load.");
      return;
    }

    // Stronger guard: refuse until the BROWSER has actually painted
    // the new image. handleImageLoad updates paintedInstanceRef with
    // the specific instance that fired its onLoad. Until paint, the
    // labeler's eyes may still be on the previous IC even though
    // currentInstance has moved on — identity check kills this.
    if (paintedInstanceRef.current !== currentInstance) {
      setValidationError(
        "Please wait — image still loading. Try again in a moment."
      );
      return;
    }

    setIsSubmitting(true);
    setError("");

    // Freeze the instance at click time. currentInstance may advance
    // after this point (a prefetch race or accidental Enter); the POST
    // body, results modal, and any cross-check all read from
    // submittingInstance, which can't change underneath them.
    const submitted = currentInstance;
    setSubmittingInstance(submitted);

    // If hash hasn't been computed yet (rare — fetch cache hit or
    // base-case race), wait for it. Backend tolerates an empty hash
    // but we'd rather send the real one for a tighter cross-check.
    let hash = submitted.hash;
    if (!hash && typeof submitted.ensureHashFromCache === "function") {
      hash = await submitted.ensureHashFromCache();
    }

    let tags = [];

    if (brain) tags.push("Brain");
    if (muscle) tags.push("Muscle");
    if (eye) tags.push("Eye");
    if (heart) tags.push("Heart");
    if (linenoise) tags.push("Line Noise");
    if (channoise) tags.push("Channel Noise");
    if (other) tags.push("Other");
    if (unsure) tags.push("Unsure");

    try {
      const response = await axios.post(
        "/api/components/submit",
        {
          name: submitted.filename,
          // Cross-check value: filename of the bytes the user actually
          // saw. Backend rejects with 409 if this disagrees with `name`.
          displayedFile: submitted.filename,
          // Bytes-hash of the painted image. Backend re-hashes `name`
          // from Dropbox; mismatch → 409. This is the last line of
          // defense against any state/pixel desync the client missed.
          displayedHash: hash || "",
          email: context.Email,
          tags: tags,
          domain: context.Domain,
          weight: context.Weight,
        },
        {
          timeout: 15000,
        }
      );

      if (response.data) {
        console.log("Submission successful:", response.data);
        setStatus("success");
        setValidationError("");

        prefetchNextImage(context.Email);
      } else {
        throw new Error("Empty response from server");
      }
    } catch (err) {
      console.error("Submission error:", err);
      // Drift caught by the backend cross-check. Silent UX: the labeler
      // can't fix this from their side anyway (it's a browser/render
      // issue), so we don't interrupt them. The save was rejected at
      // the backend (no Component write) and the attempt is recorded
      // in SubmitLog with status="rejected_drift" / "rejected_hash_mismatch".
      // From the labeler's perspective the submit looked normal; from
      // the data side, the wrong label never landed. Investigation
      // happens via the audit log, not via the user.
      if (err.response?.status === 409) {
        console.warn(
          "Submit silently rejected by backend cross-check:",
          err.response?.data
        );
        setStatus("success");
        setValidationError("");
        prefetchNextImage(context.Email);
      } else {
        setStatus("failed");
        setError(
          err.response?.data?.message ||
            err.message ||
            "Submission failed. Please try again."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get next image
  const getNext = () => {
    console.log("Getting next image...");
    setStatus("");
    setError("");
    setValidationError("");
    setRetryCount(0);
    // Don't dispose submittingInstance until the next IC is committed —
    // it's the source for the success modal until that point. The
    // assignment in getImage() below handles disposal in the right
    // order.
    setSubmittingInstance(null);
    getImage();
  };

  // Keyboard shortcuts setup
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ignore when typing
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
        return;

      const key = e.key.toLowerCase();

      // Number shortcuts
      const numberMap = {
        1: setBrain,
        2: setMuscle,
        3: setEye,
        4: setHeart,

        6: setLinenoise,
        7: setChannoise,
        8: setOther,
        9: setUnsure,
      };

      // Letter shortcuts
      const letterMap = {
        b: setBrain,
        m: setMuscle,
        e: setEye,
        h: setHeart,
        s: setSkin,
        l: setLinenoise,
        c: setChannoise,
        o: setOther,
        u: setUnsure,
      };

      if (numberMap[key]) {
        numberMap[key]((prev) => !prev);
        setValidationError("");
        return;
      }

      if (letterMap[key]) {
        letterMap[key]((prev) => !prev);
        setValidationError("");
        return;
      }

      // Actions
      switch (key) {
        case "enter":
          status ? getNext() : submitResults();
          break;
        case "x":
          if (!status && skipCooldown === 0) handleSkip();
          break;
        case "c":
          if (!status) clearAllSelections();
          break;
        case "h":
          setShowDetailedHelp((prev) => !prev);
          break;
        case "escape":
          setShowDetailedHelp(false);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [
    status,
    isSubmitting,
    skipCooldown,
    brain,
    muscle,
    eye,
    heart,

    linenoise,
    channoise,
    other,
    unsure,
  ]);

  // Unmount cleanup — dispose any live ImageInstances. The instances
  // own their blob URLs, so this is the single place memory leaks could
  // come from after the refactor.
  //
  // The cleanup must read the LATEST values (state captured by the
  // useEffect closure would be stale because we want empty deps —
  // re-running this effect on every advance would dispose live
  // instances). Mirror state into refs on every render and read from
  // the refs at unmount.
  const liveCurrentRef = useRef(null);
  const liveNextRef = useRef(null);
  const liveSubmittingRef = useRef(null);
  liveCurrentRef.current = currentInstance;
  liveNextRef.current = nextInstance;
  liveSubmittingRef.current = submittingInstance;
  useEffect(() => {
    return () => {
      if (liveCurrentRef.current) liveCurrentRef.current.dispose();
      if (liveNextRef.current) liveNextRef.current.dispose();
      if (liveSubmittingRef.current) liveSubmittingRef.current.dispose();
    };
  }, []);

  const validateSelection = () => {
    const hasSelection =
      brain ||
      muscle ||
      eye ||
      heart ||
      linenoise ||
      channoise ||
      other ||
      unsure;

    if (!hasSelection) {
      setValidationError(
        "Please select at least one label before submitting, or press 'S' to skip."
      );
      return false;
    }

    // Scroll-to-end gate: block only when a trace is present AND is currently
    // wide enough to scroll. If there's no trace, or it fits without
    // scrolling, there's nothing to review and we never block (fail open).
    // Read the ref (not state) so a fast "label key + Enter" in the same tick
    // can't slip past a not-yet-flushed state update.
    const vp = scrollViewportRef.current;
    const traceScrollable =
      !!scrollImage &&
      !!vp &&
      vp.scrollWidth - vp.clientWidth > SCROLL_END_EPSILON;
    if (traceScrollable && !reachedScrollEndRef.current) {
      setValidationError(SCROLL_END_MESSAGE);
      return false;
    }

    setValidationError("");
    return true;
  };

  const prefetchNextImage = async (email) => {
    if (prefetchingRef.current || !email || email === "guest") return;
    // Take the lock SYNCHRONOUSLY so a second call in the same tick can't
    // sneak past while the first is awaiting.
    prefetchingRef.current = true;

    console.log("🔄 Prefetching next image...");

    try {
      const filenameResponse = await axios.get("/dropbox/imagefile", {
        params: { email },
        timeout: 30000,
      });

      const filename = filenameResponse.data;
      if (!filename) {
        throw new Error("No filename received");
      }

      console.log("📋 Next filename:", filename);

      let instance = null;

      // Try the sessionStorage blob-URL cache first.
      const cachedUrl = sessionStorage.getItem(`img_${filename}`);
      if (cachedUrl) {
        try {
          const testResponse = await fetch(cachedUrl, { method: "HEAD" });
          if (testResponse.ok) {
            console.log("✓ Next image already cached");
            instance = ImageInstance.fromCachedUrl(filename, cachedUrl);
            // Backfill the hash so submit doesn't have to wait.
            await instance.ensureHashFromCache();
          } else {
            sessionStorage.removeItem(`img_${filename}`);
          }
        } catch (_) {
          sessionStorage.removeItem(`img_${filename}`);
        }
      }

      if (!instance) {
        console.log("⬇️ Downloading next image...");
        const imageResponse = await axios.get("/dropbox/imagedata", {
          responseType: "blob",
          params: { imagefile: filename },
          timeout: 45000,
        });

        if (!imageResponse.data || imageResponse.data.size === 0) {
          throw new Error("Empty image data received for prefetch");
        }
        if (!imageResponse.data.type.startsWith("image/")) {
          throw new Error(`Invalid image type: ${imageResponse.data.type}`);
        }

        instance = await ImageInstance.fromBlob(filename, imageResponse.data);

        try {
          sessionStorage.setItem(`img_${filename}`, instance.src);
          console.log("💾 Cached next image");
        } catch (e) {
          console.warn("SessionStorage full, clearing cache");
          const keys = Object.keys(sessionStorage);
          keys
            .filter((k) => k.startsWith("img_"))
            .slice(0, 10)
            .forEach((k) => {
              const urlToRevoke = sessionStorage.getItem(k);
              if (urlToRevoke && urlToRevoke.startsWith("blob:")) {
                URL.revokeObjectURL(urlToRevoke);
              }
              sessionStorage.removeItem(k);
            });
          try {
            sessionStorage.setItem(`img_${filename}`, instance.src);
          } catch (_) {}
        }
      }

      // Replace any previously-prefetched (and now superseded) instance.
      // We use the functional setState form so we never close over a
      // stale value of nextInstance from this closure.
      setNextInstance((prev) => {
        if (prev && prev !== instance) prev.dispose();
        return instance;
      });

      console.log("✅ Next image prefetched successfully");
    } catch (error) {
      console.error("❌ Prefetch error (non-critical):", error);
    } finally {
      prefetchingRef.current = false;
    }
  };

  const getImage = async (isRetry = false) => {
    // Fast path: a prefetched instance is ready — promote it to current
    // in a single atomic transition.
    if (nextInstance && !isRetry) {
      console.log("⚡ Using prefetched image - INSTANT LOAD!");
      const incoming = nextInstance;

      // Functional updates so we don't close over stale state, and so
      // disposal of the outgoing instance happens exactly once.
      setNextInstance(null);
      setCurrentInstance((prev) => {
        if (prev && prev !== incoming) prev.dispose();
        return incoming;
      });

      clearAllSelections();

      const email = localStorage.getItem("Email");
      if (email && email !== "guest") {
        setTimeout(() => prefetchNextImage(email), 100);
      }

      return;
    }

    if (isLoading) {
      console.log("Already loading, skipping request");
      return;
    }

    setIsLoading(true);
    if (!isRetry) {
      setError("");
      setValidationError("");
      // Clear currentInstance during the load window. Combined with the
      // submit guard (refuses when currentInstance is null), this makes
      // it impossible for a fast labeler to submit between ICs.
      setCurrentInstance((prev) => {
        if (prev) prev.dispose();
        return null;
      });
      paintedInstanceRef.current = null;
    }

    clearAllSelections();

    const email = localStorage.getItem("Email");

    if (!email || email === "guest") {
      setError("You must be logged in to label components.");
      setIsLoading(false);
      return;
    }

    console.log("Getting image file for:", email);

    try {
      // On retry, hold onto the same filename if we still have a
      // current instance (we don't, since the load-window clear above
      // disposed it on first try). Otherwise re-resolve.
      let filename =
        isRetry && currentInstance ? currentInstance.filename : null;

      if (!filename) {
        const filenameResponse = await axios.get("/dropbox/imagefile", {
          params: { email: email },
          timeout: 30000,
        });

        console.log("Got filename:", filenameResponse.data);

        if (!filenameResponse.data) {
          throw new Error("No filename received from server");
        }

        filename = filenameResponse.data;
      }

      let instance = null;

      // Cache hit branch (skip on retry — if we got here on retry the
      // cached URL is suspect).
      if (!isRetry) {
        const cachedUrl = sessionStorage.getItem(`img_${filename}`);
        if (cachedUrl) {
          console.log("✓ Using cached image URL");

          try {
            const testResponse = await fetch(cachedUrl, { method: "HEAD" });
            if (testResponse.ok) {
              instance = ImageInstance.fromCachedUrl(filename, cachedUrl);
              await instance.ensureHashFromCache();
            } else {
              console.log("Cached URL invalid, removing from cache");
              sessionStorage.removeItem(`img_${filename}`);
            }
          } catch (e) {
            console.log("Cached URL test failed:", e);
            sessionStorage.removeItem(`img_${filename}`);
          }
        }
      }

      // Cache miss — fetch the bytes.
      if (!instance) {
        console.log("✗ Fetching image from server:", filename);
        const imageResponse = await axios.get("/dropbox/imagedata", {
          responseType: "blob",
          params: { imagefile: filename },
          timeout: 45000,
        });

        console.log("Got image data, size:", imageResponse.data.size, "bytes");

        if (!imageResponse.data || imageResponse.data.size === 0) {
          throw new Error("Empty image data received");
        }
        if (!imageResponse.data.type.startsWith("image/")) {
          throw new Error(`Invalid image type: ${imageResponse.data.type}`);
        }

        instance = await ImageInstance.fromBlob(filename, imageResponse.data);

        if (!isRetry) {
          try {
            sessionStorage.setItem(`img_${filename}`, instance.src);
          } catch (e) {
            console.warn("SessionStorage full, clearing cache");
            const keys = Object.keys(sessionStorage);
            keys
              .filter((k) => k.startsWith("img_"))
              .slice(0, 10)
              .forEach((k) => {
                const urlToRevoke = sessionStorage.getItem(k);
                if (urlToRevoke && urlToRevoke.startsWith("blob:")) {
                  URL.revokeObjectURL(urlToRevoke);
                }
                sessionStorage.removeItem(k);
              });
            try {
              sessionStorage.setItem(`img_${filename}`, instance.src);
            } catch (_) {}
          }
        }
      }

      setCurrentInstance((prev) => {
        if (prev && prev !== instance) prev.dispose();
        return instance;
      });
      setIsLoading(false);
      setRetryCount(0);

      prefetchNextImage(email);
    } catch (error) {
      console.error("Error loading image:", error);

      if (retryCount < maxRetries) {
        console.log(`Retrying... Attempt ${retryCount + 1} of ${maxRetries}`);
        setRetryCount(retryCount + 1);
        setIsLoading(false);

        setTimeout(() => {
          getImage(true);
        }, 1000 * (retryCount + 1));
        return;
      }

      let errorMsg = "Unable to load image. ";

      if (error.code === "ECONNABORTED") {
        errorMsg += "Connection timed out.";
      } else if (error.response) {
        errorMsg += error.response.data?.error || "Server error occurred.";
      } else if (error.request) {
        errorMsg += "No response from server.";
      } else {
        errorMsg += error.message || "Unknown error.";
      }

      setError(errorMsg);
      setIsLoading(false);
      setRetryCount(0);
      setCurrentInstance((prev) => {
        if (prev) prev.dispose();
        return null;
      });
    }
  };

  useEffect(() => {
    if (!status && !currentInstance && !isLoading && !nextInstance) {
      getImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the paired scroll-trace PNG whenever the current IC filename
  // changes. Dep on the filename string (stable across re-fetches of
  // the same IC, e.g. retry) so we don't refetch the scroll trace
  // unnecessarily. Behavior matches the pre-refactor `[labelFile]` dep.
  useEffect(() => {
    const labelFile = currentInstance?.filename || "";
    if (!labelFile) {
      if (currentScrollBlobUrl.current) {
        URL.revokeObjectURL(currentScrollBlobUrl.current);
        currentScrollBlobUrl.current = null;
      }
      setScrollImage(null);
      return;
    }

    let cancelled = false;
    const cacheKey = `scroll_${labelFile}`;
    const cachedUrl = sessionStorage.getItem(cacheKey);

    // Clear the previous scroll immediately so the new base image isn't
    // rendered with the stale scroll on top during the fetch window.
    if (!cachedUrl) {
      if (currentScrollBlobUrl.current) {
        URL.revokeObjectURL(currentScrollBlobUrl.current);
        currentScrollBlobUrl.current = null;
      }
      setScrollImage(null);
    }

    const applyScrollUrl = (url) => {
      if (cancelled) return;
      if (
        currentScrollBlobUrl.current &&
        currentScrollBlobUrl.current !== url
      ) {
        URL.revokeObjectURL(currentScrollBlobUrl.current);
      }
      currentScrollBlobUrl.current = url;
      setScrollImage(url);
    };

    const fetchScroll = async () => {
      if (cachedUrl) {
        try {
          const head = await fetch(cachedUrl, { method: "HEAD" });
          if (head.ok) {
            applyScrollUrl(cachedUrl);
            return;
          }
          sessionStorage.removeItem(cacheKey);
        } catch (_) {
          sessionStorage.removeItem(cacheKey);
        }
      }

      try {
        const resp = await axios.get("/dropbox/scrolldata", {
          responseType: "blob",
          params: { imagefile: labelFile },
          timeout: 60000,
          validateStatus: (s) => s === 200,
        });
        if (!resp.data || resp.data.size === 0) return;
        const url = URL.createObjectURL(resp.data);
        try {
          sessionStorage.setItem(cacheKey, url);
        } catch (_) {
          // sessionStorage full — ignore, we still have the live blob URL
        }
        applyScrollUrl(url);
      } catch (err) {
        // 404 or network error: hide overlay silently, base image still works
        if (cancelled) return;
        setScrollImage(null);
      }
    };

    fetchScroll();
    return () => {
      cancelled = true;
    };
  }, [currentInstance?.filename]);

  // Custom always-visible horizontal scrollbar for the scroll-trace overlay.
  // macOS hides native scrollbars when idle regardless of ::-webkit-scrollbar
  // styling, so we draw our own track/thumb and keep them in sync with the
  // hidden scroll position of .scroll-viewport.
  useEffect(() => {
    if (!scrollImage) {
      // No trace for this IC — nothing to review, so the gate is satisfied.
      reachedScrollEndRef.current = true;
      setReachedScrollEnd(true);
      return;
    }
    // A new trace mounted: reset the gate for this IC. It re-latches when the
    // labeler reaches the end (onScroll) or via the Scroll-to-End button, and
    // is auto-satisfied below if the trace turns out not to be scrollable.
    reachedScrollEndRef.current = false;
    setReachedScrollEnd(false);

    const vp = scrollViewportRef.current;
    const track = scrollTrackRef.current;
    const thumb = scrollThumbRef.current;
    if (!vp || !track || !thumb) return;

    const updateThumb = () => {
      const vw = vp.clientWidth;
      const cw = vp.scrollWidth;
      const tw = track.clientWidth;
      if (!vw || !cw || !tw) return;
      const ratio = Math.min(1, vw / cw);
      const thumbW = Math.max(24, tw * ratio);
      const maxScroll = cw - vw;
      const pos = maxScroll > 0 ? (vp.scrollLeft / maxScroll) * (tw - thumbW) : 0;
      thumb.style.width = `${thumbW}px`;
      thumb.style.transform = `translateX(${pos}px)`;
    };

    const setScrollFromClientX = (clientX, thumbOffset) => {
      const tw = track.clientWidth;
      const thumbW = thumb.getBoundingClientRect().width;
      const trackLeft = track.getBoundingClientRect().left;
      const newLeft = Math.max(
        0,
        Math.min(tw - thumbW, clientX - trackLeft - thumbOffset)
      );
      const maxLeft = tw - thumbW;
      const maxScroll = vp.scrollWidth - vp.clientWidth;
      vp.scrollLeft = maxLeft > 0 ? (newLeft / maxLeft) * maxScroll : 0;
    };

    // Latch "reached the end" once the viewport is scrolled to its far-right
    // edge. Fires for wheel, thumb-drag, track-click, touch, AND the
    // programmatic Scroll-to-End button — all of which dispatch a native
    // scroll event — so "slide straight to the end" satisfies it too.
    const detectEnd = () => {
      if (reachedScrollEndRef.current) return;
      const maxScroll = vp.scrollWidth - vp.clientWidth;
      if (
        maxScroll > SCROLL_END_EPSILON &&
        maxScroll - vp.scrollLeft <= SCROLL_END_EPSILON
      ) {
        markScrollEndReached();
      }
    };
    // After the trace image has laid out, if it isn't actually wide enough to
    // scroll there's nothing to review — auto-satisfy so submit isn't blocked
    // forever on a non-scrollable trace.
    const autoSatisfyIfNotScrollable = () => {
      if (reachedScrollEndRef.current) return;
      if (vp.scrollWidth - vp.clientWidth <= SCROLL_END_EPSILON) {
        markScrollEndReached();
      }
    };

    const onScroll = () => {
      updateThumb();
      detectEnd();
    };
    const onResize = () => updateThumb();

    let dragOffset = 0;
    const onThumbDown = (e) => {
      e.preventDefault();
      const rect = thumb.getBoundingClientRect();
      dragOffset = e.clientX - rect.left;
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    };
    const onMove = (e) => setScrollFromClientX(e.clientX, dragOffset);
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    const onTrackDown = (e) => {
      if (e.target === thumb) return;
      const thumbW = thumb.getBoundingClientRect().width;
      setScrollFromClientX(e.clientX, thumbW / 2);
    };

    // Touch equivalents — without these, finger drags on the thumb / track
    // do nothing on mobile (browsers don't synthesize mousedown from touch).
    const onThumbTouchStart = (e) => {
      if (!e.touches || !e.touches[0]) return;
      e.preventDefault();
      const rect = thumb.getBoundingClientRect();
      dragOffset = e.touches[0].clientX - rect.left;
      document.addEventListener("touchmove", onTouchMove, { passive: false });
      document.addEventListener("touchend", onTouchEnd);
      document.addEventListener("touchcancel", onTouchEnd);
    };
    const onTouchMove = (e) => {
      if (!e.touches || !e.touches[0]) return;
      e.preventDefault();
      setScrollFromClientX(e.touches[0].clientX, dragOffset);
    };
    const onTouchEnd = () => {
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
    const onTrackTouchStart = (e) => {
      if (!e.touches || !e.touches[0]) return;
      if (e.target === thumb) return;
      const thumbW = thumb.getBoundingClientRect().width;
      setScrollFromClientX(e.touches[0].clientX, thumbW / 2);
    };

    // Mouse wheel over the viewport pans the trace horizontally.
    // deltaY is the dominant axis on a normal mouse; we apply it as scrollLeft
    // and preventDefault so the page doesn't scroll vertically instead.
    const onWheel = (e) => {
      const delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (delta === 0) return;
      e.preventDefault();
      vp.scrollLeft += delta;
    };

    vp.addEventListener("scroll", onScroll, { passive: true });
    vp.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("resize", onResize);
    thumb.addEventListener("mousedown", onThumbDown);
    track.addEventListener("mousedown", onTrackDown);
    thumb.addEventListener("touchstart", onThumbTouchStart, { passive: false });
    track.addEventListener("touchstart", onTrackTouchStart, { passive: true });

    // initial sync + one more after image loads (width changes). We only
    // evaluate the non-scrollable auto-satisfy once the image has actually
    // laid out (img.complete / load) so a not-yet-measured trace never
    // wrongly counts as "nothing to scroll".
    updateThumb();
    const img = vp.querySelector("img");
    const onImgReady = () => {
      updateThumb();
      autoSatisfyIfNotScrollable();
    };
    if (img) {
      if (img.complete) onImgReady();
      else img.addEventListener("load", onImgReady, { once: true });
    }
    // also resync shortly after mount in case layout settles (visual only —
    // NOT used for the auto-satisfy decision, to keep it fail-open)
    const t = setTimeout(updateThumb, 100);

    return () => {
      clearTimeout(t);
      vp.removeEventListener("scroll", onScroll);
      vp.removeEventListener("wheel", onWheel);
      window.removeEventListener("resize", onResize);
      thumb.removeEventListener("mousedown", onThumbDown);
      track.removeEventListener("mousedown", onTrackDown);
      thumb.removeEventListener("touchstart", onThumbTouchStart);
      track.removeEventListener("touchstart", onTrackTouchStart);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [scrollImage]);

  // The structural fix: the closure captures the SPECIFIC instance.
  // Stale onLoad events from prior images fire on different (already-
  // disposed) instances; the identity stamp on paintedInstanceRef
  // tells the submit guard exactly what's painted.
  //
  // Combined with `key={instance.id}` on the <img>, React mounts a
  // fresh DOM node per instance, so stale loads from unmounted nodes
  // don't reach our handler at all. This is the belt + suspenders
  // that closes the drift hole.
  const handleImageLoadFor = (instance) => () => {
    console.log("✓ Image rendered:", instance.filename);
    setError("");
    paintedInstanceRef.current = instance;
  };

  const handleImageError = (e) => {
    console.error("✗ Image failed to render");

    if (retryCount < maxRetries) {
      console.log(
        `Image render failed, retrying... Attempt ${
          retryCount + 1
        } of ${maxRetries}`
      );
      setRetryCount(retryCount + 1);
      setCurrentInstance((prev) => {
        if (prev) prev.dispose();
        return null;
      });

      setTimeout(() => {
        getImage(true);
      }, 1000 * (retryCount + 1));
    } else {
      console.log("Max retries exceeded, loading next image");
      setRetryCount(0);
      setTimeout(() => getNext(), 500);
    }
  };

  // Label options with custom line-art icons (inherit color via currentColor)
  const labelOptions = [
    {
      key: "brain",
      label: "Brain",
      icon: <BrainIcon />,
      shortcut: "1",
      color: "#6366F1",
      state: brain,
      setter: setBrain,
    },
    {
      key: "muscle",
      label: "Muscle",
      icon: <MuscleIcon />,
      shortcut: "2",
      color: "#EF4444",
      state: muscle,
      setter: setMuscle,
    },
    {
      key: "eye",
      label: "Eye",
      icon: <EyeIcon />,
      shortcut: "3",
      color: "#3B82F6",
      state: eye,
      setter: setEye,
    },
    {
      key: "heart",
      label: "Heart",
      icon: <HeartIcon />,
      shortcut: "4",
      color: "#EC4899",
      state: heart,
      setter: setHeart,
    },

    {
      key: "linenoise",
      label: "Line Noise",
      icon: <LineNoiseIcon />,
      shortcut: "6",
      color: "#10B981",
      state: linenoise,
      setter: setLinenoise,
    },
    {
      key: "channoise",
      label: "Chan Noise",
      icon: <ChanNoiseIcon />,
      shortcut: "7",
      color: "#06B6D4",
      state: channoise,
      setter: setChannoise,
    },
    {
      key: "other",
      label: "Other",
      icon: <OtherIcon />,
      shortcut: "8",
      color: "#F59E0B",
      state: other,
      setter: setOther,
    },
    {
      key: "unsure",
      label: "Unsure",
      icon: <UnsureIcon />,
      shortcut: "9",
      color: "#6B7280",
      state: unsure,
      setter: setUnsure,
    },
  ];

  const renderLabelingInterface = () => (
    <div className="main-wrapper">
      {/* Collapse/expand the left sidebar for more labeling room (md+ only —
          the sidebar is already hidden on small screens). */}
      <button
        type="button"
        className="sidebar-toggle-btn d-none d-md-flex"
        onClick={() => setSidebarCollapsed((v) => !v)}
        title={sidebarCollapsed ? "Show menu" : "Hide menu"}
        aria-label={sidebarCollapsed ? "Show menu" : "Hide menu"}
        style={{ left: sidebarCollapsed ? 8 : "calc(var(--sidebar-width) + 8px)" }}
      >
        {sidebarCollapsed ? "»" : "«"}
      </button>

      {/* Header bar removed — the navbar already shows navigation + your name.
          The ↻ Update and ? Help buttons live in the shortcut strip below. */}

      {/* Detailed Help Modal */}
      {showDetailedHelp && (
        <div className="help-modal">
          <div className="help-content">
            <div className="help-header">
              <h3>Keyboard Shortcuts Guide</h3>
              <button onClick={() => setShowDetailedHelp(false)}>×</button>
            </div>
            <div className="help-body">
              <div className="help-section">
                <h4>Signal Type Labels</h4>
                <p>Press number keys to toggle signal types:</p>
                {labelOptions.map((opt) => (
                  <div key={opt.key} className="help-row">
                    <kbd>{opt.shortcut}</kbd>
                    <span>
                      {opt.icon} {opt.label}
                    </span>
                  </div>
                ))}
              </div>
              <div className="help-section">
                <h4>Action Commands</h4>
                <div className="help-row">
                  <kbd>Enter</kbd>
                  <span>Submit labels or go to next image</span>
                </div>
                <div className="help-row">
                  <kbd>S</kbd>
                  <span>Skip current image without labeling</span>
                </div>
                <div className="help-row">
                  <kbd>C</kbd>
                  <span>Clear all selected labels</span>
                </div>
                <div className="help-row">
                  <kbd>H</kbd>
                  <span>Toggle this help window</span>
                </div>
                <div className="help-row">
                  <kbd>Esc</kbd>
                  <span>Close this help window</span>
                </div>
              </div>
              <div className="help-section">
                <h4>Tips</h4>
                <ul>
                  <li>Select <strong>every</strong> label that applies — an IC is not limited to one class, so check all that fit (e.g. brain + eye)</li>
                  <li>At least one label must be selected to submit</li>
                  <li>Use keyboard shortcuts for faster labeling</li>
                  <li>Images are prefetched for instant loading</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="content-container">
        {/* Alerts */}
        {error && (
          <div className="alert-box error">
            <span className="alert-icon">⚠️</span>
            <div className="alert-text">
              <strong>Error:</strong> {error}
            </div>
            <button
              className="alert-button"
              onClick={() => {
                setError("");
                getNext();
              }}
            >
              Try Next
            </button>
          </div>
        )}

        {validationError && (
          <div className="alert-box warning">
            <span className="alert-icon">ℹ️</span>
            <div className="alert-text">{validationError}</div>
            <button
              className="alert-close"
              onClick={() => setValidationError("")}
            >
              ×
            </button>
          </div>
        )}
        <div className="shortcut-strip">
          <span className="strip-title">Keyboard Shortcuts:</span>
          <span>
            <kbd>B</kbd> Brain
          </span>
          <span>
            <kbd>M</kbd> Muscle
          </span>
          <span>
            <kbd>E</kbd> Eye
          </span>
          <span>
            <kbd>H</kbd> Heart
          </span>
          <span>
            <kbd>L</kbd> Line Noise
          </span>
          <span>
            <kbd>C</kbd> Channel Noise
          </span>
          <span>
            <kbd>O</kbd> Other
          </span>
          <span>
            <kbd>U</kbd> Unsure
          </span>
          <span>
            <kbd>Enter</kbd> Submit
          </span>
          <span>
            <kbd>X</kbd> Skip
          </span>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="loading-state">
            <LoadingSpinner />
            <p>Loading signal data...</p>
          </div>
        ) : currentInstance ? (
          <div className="main-content">
            {/* Image Section - Larger */}
            <div className="image-section">
              <div className="section-header">
                <h2>Signal Visualization</h2>
              </div>
              <div className="image-container">
                <div className="zoom-wrapper">
                  {/*
                    key={currentInstance.id} → React mounts a fresh <img>
                    DOM node per ImageInstance. Stale `load` events from
                    a previous instance fire on an unmounted node and
                    React drops them, so they can never call our handler.
                    This is the structural piece of the drift fix; the
                    paintedInstanceRef === currentInstance check at submit
                    time is the runtime piece.
                  */}
                  <img
                    key={currentInstance.id}
                    ref={imageRef}
                    src={currentInstance.src}
                    alt="Medical signal"
                    className="signal-image"
                    onLoad={handleImageLoadFor(currentInstance)}
                    onError={handleImageError}
                  />
                  {scrollImage && (
                    <div className="scroll-overlay">
                      {/* Sits on the scroll strip: doubles as the "this is
                          scrollable" cue AND the jump-to-end control. Hidden
                          once the labeler has reached the end. */}
                      {!reachedScrollEnd && (
                        <button
                          type="button"
                          className="scroll-hint scroll-to-end-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleScrollToEnd();
                          }}
                          title="Jump the time course to its end (for obvious ICs)"
                        >
                          Scroll to End ⤓
                        </button>
                      )}
                      <div
                        className="scroll-viewport"
                        ref={scrollViewportRef}
                      >
                        <img
                          src={scrollImage}
                          alt="Scroll trace"
                          className="scroll-image"
                        />
                      </div>
                      {/* Frozen y-axis strip: same image, clipped to its left
                          edge, pinned over the viewport so the uV label and
                          tick numbers stay visible while the trace scrolls. */}
                      <div className="frozen-axis" aria-hidden="true">
                        <img
                          src={scrollImage}
                          alt=""
                          className="scroll-image"
                        />
                      </div>
                      <div
                        className="scroll-track"
                        ref={scrollTrackRef}
                      >
                        <div
                          className="scroll-thumb"
                          ref={scrollThumbRef}
                        />
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Control Section */}
            <div className="control-section">
              <div className="section-header">
                <h2>Classification Labels</h2>
                <button
                  className="clear-button"
                  onClick={clearAllSelections}
                  disabled={!!status}
                  title="Clear All (C)"
                >
                  Clear
                </button>
              </div>

              <p
                style={{
                  margin: "0 0 12px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#4b5563",
                }}
              >
                Select all classes that apply.
              </p>

              <div className="labels-grid">
                {labelOptions.map(
                  ({ key, label, icon, shortcut, color, state, setter }) => (
                    <button
                      key={key}
                      className={`label-button ${state ? "active" : ""}`}
                      onClick={() => {
                        setter(!state);
                        setValidationError("");
                      }}
                      disabled={!!status || isSubmitting}
                      data-shortcut={shortcut}
                      style={{
                        "--btn-color": color,
                        "--btn-color-light": `${color}22`, // very light version
                        background: state ? color : `${color}22`,
                        borderColor: state ? color : "#e5e7eb",
                        color: state ? "#fff" : "#4b5563",
                      }}
                    >
                      <span className="label-icon">{icon}</span>
                      <span className="label-text">{label}</span>
                      <kbd className="label-key">{shortcut}</kbd>
                      {state && <span className="label-check">✓</span>}
                    </button>
                  )
                )}
              </div>

              <div className="action-buttons">
                {!status ? (
                  <button
                    className="btn-primary"
                    onClick={submitResults}
                    disabled={isSubmitting}
                    title="Submit (Enter)"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Labels"}
                  </button>
                ) : (
                  <button
                    className="btn-success"
                    onClick={getNext}
                    title="Next (Enter)"
                  >
                    Next Image →
                  </button>
                )}

                {!status && (
                  <button
                    className="btn-secondary skip-btn"
                    onClick={handleSkip}
                    disabled={isSubmitting || skipCooldown > 0}
                    title="Skip (S)"
                  >
                    {skipCooldown > 0 ? skipCooldown : "Skip Image"}
                  </button>
                )}
              </div>

              {/* Inline success — matches PracticePage style */}
              {status === "success" && (
                <div
                  style={{
                    marginTop: 14,
                    padding: "12px 16px",
                    borderRadius: 8,
                    background: "rgba(5, 150, 105, 0.06)",
                    border: "1px solid rgba(5, 150, 105, 0.3)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: 13,
                      color: "#059669",
                      fontWeight: 600,
                    }}
                  >
                    ✓ Labels submitted
                  </span>
                  <LabelModal
                    open={false}
                    getStatus={true}
                    compName={
                      submittingInstance?.filename ||
                      currentInstance?.filename ||
                      ""
                    }
                    compData={null}
                    pracData={null}
                  />
                </div>
              )}

              {/* Stats */}
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <p>No image loaded</p>
          </div>
        )}
      </div>

    </div>
  );

  return (
    <AuthConsumer>
      {({ Auth, Name, TutorialDone, PracticePassed }) => {
        if (Auth === "Admin") {
          return (
            <>
              <Sidebar />
              <div className="page-content">{renderLabelingInterface()}</div>
            </>
          );
        }

        if (Auth === "User") {
          // Two-stage gate — tutorial first, then practice round
          if (!TutorialDone) {
            return (
              <>
                <Sidebar />
                <div className="page-content">
                  <div
                    style={{
                      maxWidth: 620,
                      margin: "90px auto 0",
                      padding: "36px 40px",
                      border: "1px solid rgba(8, 145, 178, 0.28)",
                      borderRadius: 18,
                      background: "rgba(255, 255, 255, 0.94)",
                      boxShadow: "0 6px 28px rgba(15, 23, 42, 0.08)",
                      textAlign: "left",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: "#0891b2",
                        marginBottom: 12,
                      }}
                    >
                      ● Tutorial required
                    </div>
                    <h2
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 600,
                        fontSize: 26,
                        color: "#0a0e1a",
                        margin: "0 0 12px",
                      }}
                    >
                      Finish the tutorial first
                    </h2>
                    <p
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 15,
                        color: "#4b5563",
                        lineHeight: 1.6,
                        marginBottom: 24,
                      }}
                    >
                      New labelers are asked to read the component-labeling
                      guide before accessing the labeling queue. It takes
                      about 10 minutes and keeps the training data
                      high-quality. Once you mark the tutorial complete at
                      the bottom of that page, the queue will unlock
                      automatically.
                    </p>
                    <a
                      href="/tutorial"
                      style={{
                        display: "inline-block",
                        background: "#0891b2",
                        border: "1px solid #0891b2",
                        color: "#ffffff",
                        padding: "12px 26px",
                        borderRadius: 8,
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 600,
                        fontSize: 13,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        textDecoration: "none",
                      }}
                    >
                      Go to tutorial →
                    </a>
                  </div>
                </div>
              </>
            );
          }

          // Tutorial done but practice not passed — show the practice gate
          if (!PracticePassed) {
            return (
              <>
                <Sidebar />
                <div className="page-content">
                  <div
                    style={{
                      maxWidth: 620,
                      margin: "90px auto 0",
                      padding: "36px 40px",
                      border: "1px solid rgba(8, 145, 178, 0.28)",
                      borderRadius: 18,
                      background: "rgba(255, 255, 255, 0.94)",
                      boxShadow: "0 6px 28px rgba(15, 23, 42, 0.08)",
                      textAlign: "left",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: "#0891b2",
                        marginBottom: 12,
                      }}
                    >
                      ● Practice round required
                    </div>
                    <h2
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 600,
                        fontSize: 26,
                        color: "#0a0e1a",
                        margin: "0 0 12px",
                      }}
                    >
                      Pass the practice round first
                    </h2>
                    <p
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 15,
                        color: "#4b5563",
                        lineHeight: 1.6,
                        marginBottom: 24,
                      }}
                    >
                      Before you can label live data, you need to complete the
                      10-IC practice round. Your score is reported as feedback
                      after you finish — there's no minimum to pass.
                    </p>
                    <a
                      href="/practice"
                      style={{
                        display: "inline-block",
                        background: "#0891b2",
                        border: "1px solid #0891b2",
                        color: "#ffffff",
                        padding: "12px 26px",
                        borderRadius: 8,
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 600,
                        fontSize: 13,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        textDecoration: "none",
                      }}
                    >
                      Go to practice →
                    </a>
                  </div>
                </div>
              </>
            );
          }

          return (
            <>
              <Sidebar />
              <div className="page-content">{renderLabelingInterface()}</div>
            </>
          );
        }

        // Guest fallback
        return (
          <>
            <Sidebar />
            <div className="page-content">
              <div className="auth-container">
                <div className="auth-card">
                  <h2>Authentication Required</h2>
                  <p>
                    Please log in to access the Medical Signal Classification
                    System.
                  </p>
                  <div className="auth-buttons">
                    <a href="/login" className="btn-auth primary">
                      Log In
                    </a>
                    <a href="/signup" className="btn-auth secondary">
                      Sign Up
                    </a>
                    <a href="/practice" className="btn-auth outline">
                      Practice Mode
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      }}
    </AuthConsumer>
  );
};

export default LabelPage;
